<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard 
            class="max-h-[calc(100vh-80px)]"
            :ui="{
                rounded: 'rounded-lg',
                body: { 
                    base: creatingCustomer ? 'overflow-visible' : 'overflow-auto',
                    padding: (!creatingCustomer && (!search || searching)) ? '!p-0' : 'px-4 py-5 sm:p-6'
                },
                header: { background: 'bg-gray-50', base: (!creatingCustomer && (!search || searching)) ? '!rounded-md' : 'rounded-t-md' },
                footer: { background: 'bg-gray-50 rounded-b-md' },
            }" 
        >
            <template #header>
                <div class="space-y-4">
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            Switch Customer
                        </h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                    </div>
                    <UInput
                        v-if="!creatingCustomer"
                        autofocus
                        v-model="search"
                        :loading="searching"
                        size="lg" 
                        placeholder="Search by name, phone, or credential fields..."
                        icon="i-ph-magnifying-glass-duotone"
                    />
                </div>
            </template>

            <template #default v-if="creatingCustomer || (search && !searching)">
                <UForm v-if="creatingCustomer" ref="form" @submit="onCreateCustomer" :schema="schema" :state="state" :validate-on="['submit']">
                    <div class="grid grid-cols-6 gap-x-4 gap-y-6">
                        <UFormGroup class="col-span-3" name="first_name" label="First Name" required>
                            <UInput v-model="state.first_name" type="text" size="lg" />
                        </UFormGroup>
                        <UFormGroup class="col-span-3" name="last_name" label="Last Name" required>
                            <UInput v-model="state.last_name" type="text" size="lg" />
                        </UFormGroup>
                        <UFormGroup class="col-span-6" name="middle_name" label="Father's Name" required>
                            <UInput v-model="state.middle_name" type="text" size="lg" />
                        </UFormGroup>
                        <UFormGroup class="col-span-2" name="birth_year" label="Year of Birth" required>
                            <UInput v-model="state.birth_year" type="number" size="lg" />
                        </UFormGroup>
                        <UFormGroup class="col-span-2" name="gender" label="Gender" required>
                            <USelectMenu v-model="state.gender" :options="genders" value-attribute="value" size="lg" trailing />
                        </UFormGroup>
                        <UFormGroup class="col-span-2" name="blood_type" label="Blood Type">
                            <USelectMenu v-model="state.blood_type" :options="bloodTypes" value-attribute="value" size="lg" trailing />
                        </UFormGroup>
                    </div>
                </UForm>
    
                <template v-else-if="search && !searching">
                    <div v-if="!customers.length" class="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
                        <UIcon name="i-ph-magnifying-glass-minus-duotone" class="mx-auto size-7 text-gray-400" />
                        <h3 class="mt-2 text-sm font-medium text-gray-900">No results</h3>
                        <p class="mt-1 text-xs text-gray-500">Try a different search term, or create a new customer.</p>
                        <div class="mt-4">
                            <UButton
                                @click="creatingCustomer = true"
                                icon="i-ph-plus-bold"
                                label="New Customer"
                                color="black"
                            />
                        </div>
                    </div>
    
                    <div v-else class="flex-1">
                        <button
                            type="button"
                            v-for="customer in customers" 
                            :key="customer.id" 
                            @click="onChooseCustomer(customer.id)" 
                            class="-mt-px relative w-full px-5 py-4 border first:rounded-t-md last:rounded-b-md space-y-3 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-primary transition-colors"
                        >
                            <div class="flex items-center space-x-3">
                                <Avatar 
                                    class="shrink-0"
                                    size="md"
                                    :path="customer.avatar" 
                                    :placeholder="[customer.first_name, customer.last_name].filter(Boolean).join(' ')" 
                                />
    
                                <div class="flex-1 leading-4 text-left">
                                    <p class="font-medium text-sm">{{ [customer.first_name, customer.middle_name, customer.last_name].filter(Boolean).join(' ') }}</p>
                                    <span class="text-xs capitalize">{{ [customer.gender, age(customer.birth_date), customer.blood_type].filter(Boolean).join(' · ') }}</span>
                                </div>
    
                                <BadgeCredentialSource
                                    v-if="customer.matchedCredential"
                                    :fields="customer.matchedCredential.fields"
                                    class="shrink-0"
                                />

                                <UIcon v-if="switchingCaller && selectedCustomerId == customer.id" name="i-ph-circle-notch" class="size-5 text-gray-500 animate-spin-slow" />
                                <UIcon v-else-if="!switchingCaller" name="i-ph-caret-right-bold" class="size-3 text-gray-400" />
                            </div>
                            <div class="space-y-1 text-sm">
                                <Field v-if="customer.parent" inline label="Parent / Guardian" label-class="text-[11px] uppercase text-gray-400">
                                    <span>{{ [customer.parent.first_name, customer.parent.middle_name, customer.parent.last_name].filter(Boolean).join(' ') }}</span>
                                </Field>
                                <Field v-if="customer.metadata?.emergency_phone" inline label="Emergency Phone" label-class="text-[11px] uppercase text-gray-400">
                                    <PhoneLabel :value="customer.metadata.emergency_phone" />
                                </Field>
                                <Field v-if="customer.metadata?.emergency_contact" inline label="Emergency Contact" truncate label-class="text-[11px] uppercase text-gray-400">
                                    <span>{{ customer.metadata.emergency_contact }}</span>
                                </Field>
                                <Field v-if="customer.phone" inline label="Registered Phone" label-class="text-[11px] uppercase text-gray-400">
                                    <PhoneLabel :value="customer.phone" />
                                </Field>
                                <Field v-if="customer.metadata?.alternate_phone" inline label="Alternate Phone" label-class="text-[11px] uppercase text-gray-400">
                                    <PhoneLabel :value="customer.metadata.alternate_phone" />
                                </Field>
                                <Field v-if="customer.email" inline label="Registered Email" label-class="text-[11px] uppercase text-gray-400">
                                    <span>{{ customer.email }}</span>
                                </Field>
                                <Field v-if="customer?.policy_holder" inline label="Policy Holder" label-class="text-[11px] uppercase text-gray-400">
                                    <span>{{ customer.policy_holder }}</span>
                                </Field>
                                <Field v-if="customer.tags?.length" inline label="Tags" label-class="text-[11px] uppercase text-gray-400">
                                    <span>{{ customer.tags.join(', ') }}</span>
                                </Field>
                            </div>
                        </button>
                    </div>
                </template>
            </template>

            <template #footer v-if="creatingCustomer || (search && !searching && customers.length)">
                <div v-if="creatingCustomer" class="flex items-center justify-end space-x-2">
                    <UButton @click="dismiss()" size="lg" color="white">
                        Cancel
                    </UButton>
                    <UButton @click="form.submit()" size="lg" color="black">
                        Switch
                    </UButton>
                </div>
                <UButton
                    v-else
                    @click="creatingCustomer = true"
                    :disabled="switchingCaller"
                    icon="i-ph-plus-bold"
                    label="Create a new customer"
                    color="black"
                    size="lg"
                    class="shrink-0"
                    block
                />
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import { BloodType, Gender, type Customer } from '~/types/customer'

const emit = defineEmits<{
    'update:open': [boolean],
    'switched': [void]
}>()

const props = defineProps<{
    open: boolean
    callId?: string
    currentCustomerId?: string
}>()

const CUSTOMER_SELECT = `
    id, number, user_id, first_name, middle_name, last_name, email, phone, avatar, birth_date, gender, blood_type, tags, policy_holder, metadata, updated_at,
    parent:parent_id (id, first_name, middle_name, last_name),
    subscriptions (id, is_active, expires_at)
`

type SearchResult = Customer & { matchedCredential?: { fields: Record<string, string> } }

const client = useSupabaseClient()
const { age } = useNumber()

const form = ref()
const state = ref<z.infer<typeof schema>>({} as z.infer<typeof schema>)
const search = ref<string>('')
const searching = ref<boolean>(false)
const searchTimeout = ref<NodeJS.Timeout>()
const customers = ref<SearchResult[]>([])
const switchingCaller = ref<boolean>(false)
const creatingCustomer = ref<boolean>(false)
const selectedCustomerId = ref<string>()

const schema = z.object({
    first_name: z.string(),
    middle_name: z.string(),
    last_name: z.string(),
    birth_year: z.number().min(1900),
    gender: z.nativeEnum(Gender),
    blood_type: z.nativeEnum(BloodType).optional()
})

const genders = [
    { label: 'Male', value: Gender.male },
    { label: 'Female', value: Gender.female }
]

const bloodTypes = Object.values(BloodType).map((value) => {
    return { label: value, value: value }
})

watch(() => props.open, (open) => {
    if (open) {
        reset()
    }
})

watch(() => search.value, (value) => {
    if (value) {
        fetchData()
    }
    else {
        reset()
    }
})

async function searchCredentialMatches(query: string): Promise<{ customerId: string; fields: Record<string, string> }[]> {
    try {
        return await $fetch<{ customerId: string; fields: Record<string, string> }[]>(
            '/api/customers/search-by-credential',
            { method: 'POST', body: { query } },
        )
    } catch (error) {
        console.error('Credential search failed', error)
        return []
    }
}

async function fetchData() {
    if (search.value) {
        searching.value = true

        if (searchTimeout.value) {
            clearTimeout(searchTimeout.value)
        }

        searchTimeout.value = setTimeout(async () => {
            const searchQuery = search.value.trim()
            const numberQuery = parseInt(search.value.replace(/[^\d]/g, ''))

            const [byField, credMatches] = await Promise.all([
                client.from('customers')
                    .select(CUSTOMER_SELECT)
                    .neq('id', props.currentCustomerId)
                    .or([
                        `fts.plfts(simple).'${searchQuery}'`,
                        `email.ilike.%${searchQuery}%`,
                        !isNaN(numberQuery) ? `phone.ilike.%${numberQuery}` : undefined,
                        !isNaN(numberQuery) && numberQuery <= 2147483647 ? `number.eq.${numberQuery}` : undefined,
                    ].filter(Boolean).join(',')),
                searchCredentialMatches(searchQuery),
            ])

            const results: SearchResult[] = [...((byField.data as SearchResult[]) ?? [])]

            const credIds = credMatches
                .map((m) => m.customerId)
                .filter((id) => id !== props.currentCustomerId)

            if (credIds.length) {
                const fieldsById = new Map(credMatches.map((m) => [m.customerId, m.fields]))
                const { data: credCustomers, error: credCustomersError } = await client.from('customers')
                    .select(CUSTOMER_SELECT)
                    .in('id', credIds)

                if (credCustomersError) {
                    console.error('Failed to load credential-matched customers', credCustomersError)
                }

                for (const customer of (credCustomers as SearchResult[]) ?? []) {
                    const fields = fieldsById.get(customer.id)
                    if (!fields) {
                        continue
                    }
                    const existing = results.find((r) => r.id === customer.id)
                    if (existing) {
                        existing.matchedCredential = { fields }
                    } else {
                        results.push({ ...customer, matchedCredential: { fields } })
                    }
                }
            }

            customers.value = results
            searching.value = false
        }, 1000)
    }
}

function onChooseCustomer(id: string) {
    if (id) {
        selectedCustomerId.value = id
        switchCaller()
    }
}

async function onCreateCustomer(event: FormSubmitEvent<z.infer<typeof schema>>) {
    const { data } = await client.from('customers')
        .insert({
            first_name: event.data.first_name,
            middle_name: event.data.middle_name,
            last_name: event.data.last_name,
            gender: event.data.gender,
            blood_type: event.data.blood_type,
            birth_date: event.data.birth_year 
                ? `1-1-${event.data.birth_year}`
                : undefined
        } as never)
        .select('id')
        .single()

    if (data) {
        selectedCustomerId.value = data.id
        switchCaller()
    }
}

async function switchCaller() {
    if (!switchingCaller.value && selectedCustomerId.value && props.callId) {
        switchingCaller.value = true

        const { error } = await client.from('calls')
            .update({ customer_id: selectedCustomerId.value } as never)
            .eq('id', props.callId)

        if (error) {
            console.error(`Could not switch caller: ${error.message}`)
        } 
        else {
            emit('switched')
            dismiss()
        }
    }
}

function reset() {
    search.value = ''
    searching.value = false
    customers.value = []
    switchingCaller.value = false
    creatingCustomer.value = false
    selectedCustomerId.value = undefined
}

function dismiss() {
    emit('update:open', false)
}
</script>